import { forwardRef, type TextareaHTMLAttributes, useRef, useEffect } from 'react';
import { cn } from '@/utils';

export interface SJTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  autoResize?: boolean;
  showCount?: boolean;
}

export const SJTextarea = forwardRef<HTMLTextAreaElement, SJTextareaProps>(
  ({ className, label, error, id, autoResize, showCount, maxLength, value, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) ?? internalRef;

    useEffect(() => {
      if (autoResize && textareaRef.current) {
        const el = textareaRef.current;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, [autoResize, textareaRef, value]);

    const charCount = typeof value === 'string' ? value.length : 0;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-[var(--sj-text-secondary)]">
            {label}
          </label>
        )}
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          maxLength={maxLength}
          className={cn(
            'min-h-[80px] w-full resize-y rounded-[12px] border border-[var(--sj-border)] bg-[var(--sj-surface)] px-4 py-3 text-sm text-[var(--sj-text-primary)] placeholder:text-[var(--sj-text-muted)] transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            autoResize && 'resize-none overflow-hidden',
            error && 'border-error-500 focus:border-error-500 focus:ring-error-500/20',
            className
          )}
          {...props}
        />
        <div className="flex items-center justify-between">
          {error && <p className="text-xs text-error-600">{error}</p>}
          {showCount && maxLength && (
            <p className="ml-auto text-xs text-[var(--sj-text-muted)]">
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

SJTextarea.displayName = 'SJTextarea';
